import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Loader2, AlertTriangle } from "lucide-react";
import { MementoLogo } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = { salon_owner: "Co-titolare", staff: "Collaboratore" };

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`)
      .then((r) => { setInfo(r.data); setName(r.data.name || ""); })
      .catch((e) => setError(e?.response?.data?.detail || "Invito non valido o scaduto"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8 || !/\d/.test(password)) {
      toast.error("Almeno 8 caratteri e 1 cifra numerica");
      return;
    }
    if (password !== confirm) { toast.error("Le password non coincidono"); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/auth/invite/${token}/accept`, { password, name: name || null });
      localStorage.setItem("sp_token", r.data.access_token);
      localStorage.setItem("sp_user", JSON.stringify(r.data.user));
      await refresh();
      toast.success("Benvenuto in Memento!");
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[color:var(--sp-bg)]">
      <div className="sp-card w-full max-w-md p-8 space-y-4" data-testid="accept-invite-page">
        <div className="flex items-center justify-center">
          <MementoLogo size="lg" />
        </div>
        {loading && (
          <div className="text-center text-sm text-[color:var(--sp-text-muted)]">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Verifica invito…
          </div>
        )}
        {error && !loading && (
          <div className="text-center space-y-3" data-testid="invite-error">
            <AlertTriangle className="h-10 w-10 text-red-500 mx-auto" />
            <div className="font-display text-lg font-black">Invito non valido</div>
            <p className="text-sm text-[color:var(--sp-text-secondary)]">{error}</p>
            <button onClick={() => nav("/login")} className="sp-btn-primary">Vai al login</button>
          </div>
        )}
        {info && !error && (
          <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
            <div className="text-center">
              <ShieldCheck className="h-8 w-8 text-[color:var(--sp-brand)] mx-auto mb-2" />
              <h1 className="font-display text-2xl font-black">Sei stato invitato in <span className="text-[color:var(--sp-brand)]">{info.tenant_name}</span></h1>
              <p className="text-sm text-[color:var(--sp-text-secondary)] mt-2">
                Ruolo: <b>{ROLE_LABEL[info.role] || info.role}</b>
              </p>
            </div>
            {info.role === "staff" && info.permissions?.length > 0 && (
              <div className="rounded-lg bg-[color:var(--sp-subtle)] p-3 text-xs text-[color:var(--sp-text-secondary)]">
                <div className="font-bold mb-1">Accederai a queste sezioni:</div>
                <ul className="list-disc pl-5 space-y-0.5">
                  {info.permissions.map((p) => (
                    <li key={p}>{info.section_labels?.[p] || p}</li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <label className="text-sm font-bold">Email</label>
              <input readOnly value={info.email} className="sp-input mt-1 bg-[color:var(--sp-subtle)]" />
            </div>
            <div>
              <label className="text-sm font-bold">Nome</label>
              <input className="sp-input mt-1" value={name} onChange={(e) => setName(e.target.value)} data-testid="accept-name" />
            </div>
            <div>
              <label className="text-sm font-bold">Password</label>
              <input required type="password" minLength={8} className="sp-input mt-1" value={password}
                data-testid="accept-password"
                onChange={(e) => setPassword(e.target.value)} placeholder="min 8 caratteri + 1 cifra" />
            </div>
            <div>
              <label className="text-sm font-bold">Conferma password</label>
              <input required type="password" minLength={8} className="sp-input mt-1" value={confirm}
                data-testid="accept-password-confirm"
                onChange={(e) => setConfirm(e.target.value)} />
            </div>
            <button type="submit" disabled={submitting} data-testid="accept-submit"
              className="sp-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50">
              <CheckCircle2 className="h-4 w-4" /> {submitting ? "Attivazione…" : "Attiva account ed entra"}
            </button>
            <p className="text-[11px] text-[color:var(--sp-text-muted)] text-center">
              Al primo accesso il tuo account verrà creato automaticamente in <b>{info.tenant_name}</b>.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
